import { RoleFragment, RoleSummaryFragment } from '../gql'
import { OrgData } from '../model/OrgData'
import { getSeedRoles } from '../seeds/roles'

// At most this many names are ever returned: the confirmation window that
// lists them has no arbitration to make, whatever the organisation's size.
// The dev instance this feature was cadré against is small and not
// representative — the real orgs live on CartoGroov, unreachable from here
// (docs/features/gouv-03-passage-en-stricte.md, "Réserve").
const MAX_CIRCLES_WITHOUT_LEAD_LINK = 5

// What the "passer en Stricte" confirmation window needs to render, computed
// once so the window itself stays a plain render with no reasoning of its own
// (see phase-1 of aidd_docs/tasks/2026_08/2026_08_09_gouv-03-passage-en-stricte).
export interface StrictSwitchSummary {
  circlesWithoutLeadLink: {
    // The first MAX_CIRCLES_WITHOUT_LEAD_LINK names, alphabetical.
    names: string[]
    // How many more exist beyond those names (0 when there are none, or few
    // enough to fit already).
    remainingCount: number
  }
  // The org's existing base roles, sorted by name: shown so the person can
  // judge for themselves whether one already plays a canonical part, rather
  // than the tool guessing (see proposedRoles below).
  existingBaseRoles: RoleSummaryFragment[]
  // The four canonical roles, exactly as seeds/roles.ts defines them — never
  // deduced from existingBaseRoles.
  proposedRoles: Omit<RoleFragment, 'id'>[]
}

// Builds the state-of-play a "passer en Stricte" confirmation window shows,
// from the org's current data. `orgId` only stamps the proposed roles
// (OrgData carries no org id of its own); it changes nothing else.
export function getStrictSwitchSummary(
  orgData: OrgData,
  orgId: string
): StrictSwitchSummary {
  // A circle is an entity with at least one child — OrgData.isCircle — never
  // `role.singleMember === false`. That flag means "may have several
  // holders", not "is a circle": trusting it once counted 67 entities in an
  // org that held 12 real circles, an error made and corrected during this
  // feature's cadrage (Yocoop, 09/08/2026). Narrowing to roles that are
  // neither a lead link nor single-member first is safe on top of that (this
  // app never lets either kind hold children — see the `subCircles` local in
  // OrgData.getCirclePermissions) but isCircle remains the one test that
  // actually decides.
  const circleCandidates = orgData.circles.filter((circle) => {
    const role = orgData.getRole(circle.roleId)
    return (
      !!role &&
      !role.parentLink &&
      !role.singleMember &&
      orgData.isCircle(circle.id)
    )
  })

  // "Without a lead link" is read on the structure — whether a direct child's
  // role carries `parentLink` — never on `hasRepresentatives`. That helper
  // counts the *members* of a link, not the link itself: a freshly-created,
  // still-empty link would make it answer "no representative", and this
  // window would then reproach a gap that is already closed.
  // `OrgData.isParentLink` is private for the same reason `hasRepresentatives`
  // does not fit here — both ultimately answer about members or are scoped
  // to the model's own internals — so the structural check is inlined via
  // `getRole(child.roleId)?.parentLink`, the one property that actually says
  // "this child is a lead link", empty or not.
  const withoutLeadLink = circleCandidates.filter(
    (circle) =>
      !orgData
        .childrenOf(circle.id)
        .some((child) => orgData.getRole(child.roleId)?.parentLink)
  )

  const names = withoutLeadLink
    .map((circle) => orgData.getRole(circle.roleId)?.name ?? '')
    .sort((a, b) => a.localeCompare(b))

  const existingBaseRoles = orgData.roles
    .filter((role) => role.base)
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name))

  return {
    circlesWithoutLeadLink: {
      names: names.slice(0, MAX_CIRCLES_WITHOUT_LEAD_LINK),
      remainingCount: Math.max(0, names.length - MAX_CIRCLES_WITHOUT_LEAD_LINK),
    },
    existingBaseRoles,
    // Never deduced from existingBaseRoles: nothing in the data identifies a
    // structural role (base + parentLink + singleMember describes Leader AND
    // Représentant alike, indistinguishably), and matching by name would
    // break the day the UdN vocabulary refonte renames them — a rule this
    // repo already states in seeds/roles.ts (D4 of the cadrage).
    proposedRoles: getSeedRoles(orgId),
  }
}
